import { FC, useState } from 'react';
import { Plus } from 'lucide-react';
import { MainLayout } from 'pages/MainLayout';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { PricingRuleSheet } from 'components/pricing/PricingRuleSheet';
import { PricingRuleTesterSimple } from 'components/pricing/PricingRuleTesterSimple';
import { PricingRulesDataTable } from 'src/components/pricing/PricingRulesDataTable';
import { usePricingRules } from 'src/hooks/usePricingRules';
import { VITE_ENV } from 'src/config';
import {
  type PricingRule,
  type ContainerMaterialMatrixConfiguration,
  type ContainerPeriodPricingConfiguration,
  type PayAsYouGoPricingConfiguration,
  type TonnagePricingConfiguration,
  type AutoDiscountConfiguration,
  type DiscountConfiguration,
  ConfigurationTypeEnum,
  ConfigurationData,
} from '@libs/schemas';
import { PricingRuleFormData } from '@libs/schemas';
type FormData = PricingRuleFormData;

export const PricingDashboard: FC = () => {
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const { rules, isLoading, addRule } = usePricingRules();

  // No auto-initialization of sample rules

  const handleCreateRule = (data: FormData) => {
    // Transform CreateEditRuleForm data to PricingRule shape expected by addRule
    // In the new architecture, each rule has exactly one configuration
    const config = data.configuration;
    if (!config || !config.type) {
      throw new Error('Rule must have a configuration');
    }

    const newRule: Omit<PricingRule, '_id'> = {
      name: data.name,
      configuration: {
        type: config.type,
        // Map fields into data object based on configuration type
        data: (() => {
          // Type assertion to help TypeScript recognize the union type
          const configType = config.type;
          switch (configType) {
            case ConfigurationTypeEnum.CONTAINER_MATERIAL_MATRIX: {
              const matrixConfig = config as ContainerMaterialMatrixConfiguration;

              return matrixConfig.data;
            }
            case ConfigurationTypeEnum.CONTAINER_PERIOD_PRICING: {
              const periodConfig = config as ContainerPeriodPricingConfiguration;
              return periodConfig.data;
            }
            case ConfigurationTypeEnum.PAY_AS_YOU_GO_PRICING: {
              const payAsYouGoConfig = config as PayAsYouGoPricingConfiguration;
              return payAsYouGoConfig.data;
            }
            case ConfigurationTypeEnum.TONNAGE_PRICING: {
              const tonnageConfig = config as TonnagePricingConfiguration;
              return tonnageConfig.data;
            }
            case ConfigurationTypeEnum.AUTO_DISCOUNT: {
              const autoDiscountConfig = config as AutoDiscountConfiguration;
              return autoDiscountConfig.data;
            }
            case ConfigurationTypeEnum.DISCOUNT: {
              const discountConfig = config as DiscountConfiguration;
              return discountConfig.data;
            }
            default:
              return {};
          }
        })() as ConfigurationData,
      },
      scope: data.scope,
      precedence: data.precedence,
      effectiveFrom: data.effectiveFrom,
      effectiveTo: data.effectiveTo,
      canCombine: data.canCombine,
      organizationId: data.organizationId,
      active: data.active,
    };

    addRule(newRule);
    setIsCreateModalOpen(false);
  };

  if (isLoading) {
    return (
      <MainLayout>
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900 mx-auto mb-4"></div>
            <p>Loading pricing rules...</p>
          </div>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      {/* Header */}
      <div className="flex flex-col space-y-4 sm:flex-row sm:space-y-0 sm:justify-between sm:items-center mb-8">
        <div>
          <h1 className="text-3xl font-bold">Pricing Rules</h1>
          <p className="text-slate-500 mt-1 dark:text-slate-400">
            Manage dynamic pricing rules for your organization
          </p>
        </div>
        <div className="flex flex-col sm:flex-row gap-3">
          {VITE_ENV === 'local' && <PricingRuleTesterSimple rules={rules} />}
          <Button className="w-full sm:w-auto" onClick={() => setIsCreateModalOpen(true)}>
            <Plus className="w-4 h-4 mr-2" />
            Create Rule
          </Button>
          <PricingRuleSheet
            open={isCreateModalOpen}
            onOpenChange={setIsCreateModalOpen}
            initialData={undefined}
            onSubmit={handleCreateRule}
          />
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-slate-500 dark:text-slate-400">
              Total Rules
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-semibold">{rules.length}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-slate-500 dark:text-slate-400">
              Active Rules
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-semibold text-green-600">
              {rules.filter((r) => r.active).length}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-slate-500 dark:text-slate-400">
              Inactive Rules
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-semibold text-red-600">
              {rules.filter((r) => !r.active).length}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-slate-500 dark:text-slate-400">
              Matrix Rules
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-semibold text-blue-600">
              {
                rules.filter(
                  (r) =>
                    r.configuration &&
                    r.configuration.type === ConfigurationTypeEnum.CONTAINER_MATERIAL_MATRIX,
                ).length
              }
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Pricing Rules Table - uses existing component with built-in filtering */}
      <PricingRulesDataTable />
    </MainLayout>
  );
};
